import React, { Component } from 'react';
import { Auth } from 'aws-amplify';
import { cloneDeep, memoize, debounce } from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import withStyles from 'react-jss';
import { Translation } from 'react-i18next';
import {
  getAuthState,
  getOnboardingState,
  getCareerResults,
} from '../../store/selectors';
import {
  checkUser,
  getCurrentUserId,
  getData,
  getTenantCareers,
  getUserData,
  saveNewCareer,
  saveUserChoices,
  setAuthStatus,
  setUser,
  userLogout,
  setRegistrationInProgress,
} from '../../store/actions';
import PushNotifications from '../pushNotifications';
import Top25CareerCard from './Top25CareerCard';
import Loading from '../utility/Loading';
import { getCategoryImages } from '../utility/getCategoryImages';
import { fireGTM } from '../../gtm/gtmUtil';
import { setUpPageReloadingHandler } from './top25Util';
import { topCareersPage_GTM } from '../../gtm/datalayers/topCareersDataLayers.json';
import { closeCross_GTM } from '../../gtm/datalayers/utilDataLayers.json';
import ScrollComponent from '../utility/ScrollComponent';
import ProgressBar from '../utility/ProgressBar';
import Fade from '../utility/Fade';
import CampusBackground from '../CampusBackground';
import BrightOutlookModal from '../utility/modal/BrightOutlookModal.jsx';
import ModalController from '../utility/modal/ModalController';
import UpdateDataBanner from '../UpdateDataBanner';
import LanguageDropdown from '../LanguageDropdown';
import WindowTitle from '../utility/WindowTitle';

const continueButtonStyles = {
  color: (props) => ({
    backgroundColor: props.tenant ? props.tenant.primary : 'white',
    borderColor: props.tenant ? props.tenant.primary : 'black',

    '&:hover': {
      backgroundColor: 'white',
      border: props.tenant
        ? `2px solid ${props.tenant.primary}`
        : '2px solid black',
      color: props.tenant ? props.tenant.primary : 'black',
      borderStyle: 'solid',
    },
    '&:hover:disabled': {
      border: 'none',
      cursor: 'not-allowed',
    },
  }),
};

const bars = {
  Onboarding: {
    current: 100,
    override: false,
  },
  'Picture Game': {
    current: 100,
    override: false,
  },
  'Career Path': {
    current: 15,
    override: false,
  },
};

class Top25 extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);

    this.state = {
      userCareerInformation: [],
      addition: false,
      career1: [],
      career2: [],
      career3: [],
      otherCareers: [],
      showBrightOutlookModal: false,
      isLoaded: false,
      draggableSource: null,
      draggableIndex: null,
      outOfDateData: false,
      loading: false,
    };
  }

  secondaryBorder = {
    borderBottom: `5px solid ${this.props.tenant.secondary}`,
  };

  secondaryBorderBottom = {
    borderBottom: `5px solid ${this.props.tenant.secondary}`,
  };

  async componentDidMount() {
    this._isMounted = true;
    const userId = await this.props.getCurrentUserId(this.props.auth.jwtToken);
    await this.props.getData(userId, this.props.auth.jwtToken);

    this.setState({
      outOfDateData: this.props.onboarding.userData.outOfDateData,
    });

    const functionsToUse = {
      history: this.props.history,
      setAuthStatus: this.props.setAuthStatus,
      setUser: this.props.setUser,
      userLogout: this.props.userLogout,
      signOut: Auth.signOut,
      gtmLocation: this.props.gtmLocation,
      tenant: this.props.tenant,
    };
    const additionParams = new URLSearchParams(this.props.location.search);
    const addition = additionParams.get('addition');
    if (addition) {
      this.setState({
        addition: true,
      });
      this.getCareerInformation({ addition: true });
    } else {
      this.getCareerInformation({ addition: false });
    }

    if (
      Object.keys(this.props.onboarding.userData).length === 0 ||
      !this.props.onboarding.userData.user.hasBeenToDashboard
    ) {
      setUpPageReloadingHandler(
        this.state,
        this.props.tenant.name,
        functionsToUse
      );
    }

    const promptEvent = window.deferredPrompt;
    if (promptEvent) {
      promptEvent.prompt();
      promptEvent.userChoice.then(() => {
        window.deferredPrompt = null;
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    const additionParams = new URLSearchParams(this.props.location.search);
    const addition = additionParams.get('addition');
    if (prevProps.location && this.props.location && this._isMounted) {
      if (prevProps.location.search !== this.props.location.search) {
        if (addition) {
          this.getCareerInformation({ addition: true });
          /* eslint-disable react/no-did-update-set-state */
          this.setState({
            addition: true,
          });
        } else {
          this.setState({
            addition: false,
          });
          this.getCareerInformation({ addition: false });
        }
      }
    }

    if (this.props.currentLocale !== prevProps.currentLocale) {
      this.clearGetTenantCareersCache();
      this.getCareerInformation({
        addition: this.state.addition,
        languageChanged: true,
      });
    }
  }

  getCareersMemoized = memoize(this.props.getTenantCareers);

  clearGetTenantCareersCache() {
    this.getCareersMemoized.cache.clear();
  }

  get additionObj() {
    if (!this.state.addition) return null;

    const additionId = new URLSearchParams(this.props.location.search).get(
      'addition'
    );

    return this.getCareersMemoized(this.props.tenant.id)
      .then((careers) => {
        const addition = careers.find(
          (career) =>
            career.id === parseInt(additionId, 10) &&
            career.inactive === false &&
            career.hasMatches === true
        );

        return Promise.resolve(
          addition ? getCategoryImages([addition])[0] : null
        );
      })
      .catch(() => Promise.resolve(null));
  }

  get careerResults() {
    return this.props.userCareerGameResults;
  }

  handleDragOver = (e) => {
    e.target.classList.add('drag-over');
  };

  saveTopChoices = async () => {
    const { career1, career2, career3, otherCareers, addition } = this.state;
    const tempArr = [];
    const first = career1.length === 0 ? null : career1;
    const second = career2.length === 0 ? null : career2;
    const third = career3.length === 0 ? null : career3;
    const careersRanked = tempArr.concat(first).concat(second).concat(third);
    if (addition) {
      careersRanked.forEach(async (career) => {
        if (career && career.chosenOrder === undefined) {
          await this.props.saveNewCareer(career, this.props.auth.jwtToken);
          await this.props.saveUserChoices(
            careersRanked,
            this.props.auth.jwtToken
          );
          this.cancelAddition();
        }
      });
    }
    careersRanked.concat(otherCareers);
    await this.props.saveUserChoices(careersRanked, this.props.auth.jwtToken);
  };

  // TODO: we're making an unnecessary be call here - when we save the data, we should
  // just return it to the store at that time. there's no need to have to retrieve it again
  completeTopCareerSelection = async () => {
    try {
      this.setState({ loading: true });
      const { career1, career2, career3, otherCareers } = this.state;
      const tempArr = [];
      const careersRanked = tempArr
        .concat(career1)
        .concat(career2)
        .concat(career3)
        .concat(otherCareers);
      await this.props.saveUserChoices(careersRanked, this.props.auth.jwtToken);
      // Populate the store with a user's previously saved info
      // we have to do this so we get 'chosenOrder' with the data.
      await this.props.getData(
        this.props.onboarding.userId,
        this.props.auth.jwtToken
      );
      // We need to save the preferences in order so just give the careerChoices object
      const preferences = {};
      Object.values(this.props.onboarding.careerChoices).forEach(
        (value, index) => {
          preferences[index] = value.titleEn.toLowerCase();
        }
      );
      topCareersPage_GTM.submitChoices.preferences = preferences;
      fireGTM(topCareersPage_GTM.submitChoices);
      setTimeout(() => {
        this.props.history.push(
          `/${this.props.tenant.name.toLowerCase()}/career-path/path-pairing`
        );
      }, 100);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.setState({ loading: false }); // Set loading state back to false
    }
  };

  topThreeIncomplete = () => {
    const { career1, career2, career3 } = this.state;
    return career1.length === 0 || career2.length === 0 || career3.length === 0;
  };

  removeFromTopThree = (top3Spot) => {
    const removedItem =
      this.state[top3Spot].length === 1 && this.state[top3Spot][0];
    closeCross_GTM.component = 'current top careers';
    closeCross_GTM.event = 'link';
    closeCross_GTM.action = 'close';
    fireGTM(closeCross_GTM, 'close cross', removedItem.titleEn);
    if (this._isMounted) {
      this.setState(
        (prevState) => {
          const newOtherCareers =
            removedItem && prevState.otherCareers.concat(removedItem);
          return {
            [top3Spot]: [],
            otherCareers: newOtherCareers
              ? // eslint-disable-next-line
                newOtherCareers
              : prevState.otherCareers,
          };
        },
        () => {
          this.saveTopChoices();
        }
      );
    }
  };

  getCareerInformation = async ({ addition, languageChanged = false }) => {
    const resultsArray = [];

    if (this.props.onboarding.userData && this.careerResults.length > 0) {
      this.careerResults.map((career) => {
        return resultsArray.push(career);
      });
      if (this._isMounted) {
        this.setState(
          {
            userCareerInformation: resultsArray,
          },
          () => {
            this.populateCareerLists(addition, languageChanged);
          }
        );
      }
    }
  };

  findCareersByChosenOrder = (orderVal, list) => {
    return list.filter((career) => career.chosenOrder === orderVal);
  };

  populateCareerLists = async (addition, languageChanged = false) => {
    const { userCareerInformation } = this.state;
    const tempArr = cloneDeep(getCategoryImages(userCareerInformation));
    const searchParams = new URLSearchParams(this.props.location.search);
    const additionParams = searchParams.get('addition');

    let otherCareers = this.props.onboarding.userData.user
      .firstLoadOfCareerResults
      ? tempArr.slice(3, tempArr.length)
      : this.findCareersByChosenOrder(null, tempArr);

    if (addition && this._isMounted) {
      const additionObj = await this.additionObj;
      otherCareers = [additionObj];
    }
    if (
      (additionParams || !this.state.isLoaded || languageChanged) &&
      this._isMounted
    ) {
      this.setState({
        career1: this.props.onboarding.userData.user.firstLoadOfCareerResults
          ? tempArr.slice(0, 1)
          : this.findCareersByChosenOrder(1, tempArr),
        career2: this.props.onboarding.userData.user.firstLoadOfCareerResults
          ? tempArr.slice(1, 2)
          : this.findCareersByChosenOrder(2, tempArr),
        career3: this.props.onboarding.userData.user.firstLoadOfCareerResults
          ? tempArr.slice(2, 3)
          : this.findCareersByChosenOrder(3, tempArr),
        otherCareers,
      });
    }
    await this.props.setRegistrationInProgress(false);
    this.setState({ isLoaded: true });
  };

  cancelAddition = async () => {
    const onCancel = new URLSearchParams(this.props.location.search).get(
      'oncancel'
    );
    if (onCancel) {
      await this.props.getData(
        this.props.onboarding.userId,
        this.props.auth.jwtToken
      );
      if (
        !!this.props.onboarding.degreeChoices['1'] &&
        !!this.props.onboarding.degreeChoices['2'] &&
        !!this.props.onboarding.degreeChoices['3']
      ) {
        this.props.history.push(onCancel);
      }
      this.props.history.push('top-3');
      this.props.history.push({
        search: '',
      });
      this.setState({ isLoaded: false });
      this.getCareerInformation({ addition: false });
    }
  };

  cancelStyle = () =>
    this.state.otherCareers.length > 1 ? { display: 'none' } : {};

  pageRefresh = () => {
    this.getCareerInformation({ addition: true });
    if (this._isMounted) {
      this.setState({
        addition: true,
      });
    }
  };

  renderTopThree = (t) => {
    const career1 = cloneDeep(this.state.career1);
    const career2 = cloneDeep(this.state.career2);
    const career3 = cloneDeep(this.state.career3);
    const topThreeArr = [career1, career2, career3];
    return topThreeArr.map((career, i) => {
      return !career[0] ? (
        this.renderVacantTile(`career${i + 1}`, i)(t)
      ) : (
        <Top25CareerCard
          key={`career${i + 1}`}
          sourceList={`career${i + 1}`}
          handleDragStart={this.handleDragStart}
          tenant={this.props.tenant}
          career={career[0]}
          removeFromTopThree={this.removeFromTopThree}
          setBrightOutlookModal={this.setBrightOutlookModal}
          index={0}
          addButtonLabel={t('Reorder')}
          addButtonAriaLabel={t(
            `Click here to reorder {{itemTitle}} to a different spot within your top 3.`,
            { itemTitle: career[0].titleEn }
          )}
          index1Through25={i + 1}
          onDrop={this.handleDrop}
        />
      );
    });
  };

  renderVacantTile = (stateVal, idx) => (t) => {
    return (
      <div
        key={stateVal}
        onDragLeave={(e) => {
          e.target.style.border = 'none';
          e.target.style.borderBottom = `5px solid ${this.props.tenant.secondary}`;
        }}
        onDrop={(e) => {
          e.target.style.border = 'none';
          this.handleDrop(stateVal);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.target.style.border = `5px solid ${this.props.tenant.secondary}`;
        }}
        className="draggable-career vacant-career"
        data-cy="vacant-career"
        style={this.secondaryBorderBottom}
      >
        <span style={{ zIndex: -1000, position: 'absolute', left: -9999 }}>
          {t(
            'Top Chosen Career: Career Choice {{choiceNumber}}: Vacant. Please add a career.',
            { choiceNumber: idx + 1 }
          )}
        </span>
        <p aria-hidden="true" className="drag-here">
          {`${t('Drag Career Here')} +`}
        </p>
        <p aria-hidden="true" className="drag-here-mobile">
          {`${t('Select a Career From Below')} +`}
        </p>
      </div>
    );
  };

  handleDragStart = (source, index) => {
    this.setState({
      draggableSource: source,
      draggableIndex: index,
    });
  };

  handleDrop = async (destination) => {
    const { draggableIndex, draggableSource } = this.state;
    if (destination === draggableSource) return;
    if (destination === 'otherCareers') {
      this.removeFromTopThree(draggableSource);
    } else {
      if (this.state[destination].length > 0) {
        this.removeFromTopThree(destination);
      }
      await this.setState((prevState) => {
        const newList = prevState[draggableSource];
        const newTopThreeItem = newList.splice(draggableIndex, 1);
        return {
          [destination]: newTopThreeItem,
          [draggableSource]: newList,
        };
      });
    }
    this.setState({
      draggableIndex: null,
      draggableSource: null,
    });
    this.saveTopChoices();
  };

  renderOtherCareers = (t) => {
    const { otherCareers, isLoaded } = this.state;

    return isLoaded ? (
      otherCareers.map((career, i) => (
        <Top25CareerCard
          key={`otherCareers-${i + 1}`}
          sourceList="otherCareers"
          handleDragStart={this.handleDragStart}
          tenant={this.props.tenant}
          career={career}
          setBrightOutlookModal={this.setBrightOutlookModal}
          index={i}
          index1Through25={i + 1}
          addButtonLabel={t('Add')}
          addButtonAriaLabel={t(`Click to add {{itemTitle}} to a top 3 spot`, {
            itemTitle: career?.titleEn,
          })}
          onDrop={this.handleDrop}
        />
      ))
    ) : (
      <Loading height="100%" />
    );
  };

  setBrightOutlookModal = () => {
    this.setState((prevState) => ({
      showBrightOutlookModal: !prevState.showBrightOutlookModal,
    }));
  };

  render() {
    return (
      <Translation>
        {(t) => (
          <>
            <WindowTitle>
              {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
              {t('Career Path')} - {t('Top Careers')}
            </WindowTitle>
            {this.state.showBrightOutlookModal && (
              <BrightOutlookModal
                tenant={this.props.tenant}
                onClickParent={this.setBrightOutlookModal}
              />
            )}
            {this.state.outOfDateData && <UpdateDataBanner />}
            <ScrollComponent />
            <PushNotifications />
            <CampusBackground
              secondaryColor={this.props.tenant.secondary}
              column
            >
              <Fade in>
                {!this.state.isLoaded || this.state.loading ? (
                  <Loading height="100%" />
                ) : (
                  <>
                    <div id="top25-change-lang-header">
                      <span className="top25-change-lang-wrapper">
                        <LanguageDropdown />
                      </span>
                    </div>
                    <div
                      id="top25"
                      draggable="false"
                      className="user-content-results"
                    >
                      <div className="main-left-results bottom-border-grey">
                        <div className="header-container-results-left">
                          <div
                            className="results-text-container"
                            draggable="false"
                          >
                            <h1
                              className="results-header-text left-header"
                              draggable="false"
                            >
                              {t('Current Top Careers')}
                            </h1>
                            <p className="subheading on-mobile-only">
                              {t(
                                "Edit your top careers below.  Click the '{{symbol}}' to to remove a career and click '{{buttonLabel}}' to place in your top 3.",
                                { symbol: 'X', buttonLabel: t('Add') }
                              )}
                            </p>
                            <div
                              className="yellow-border-bar"
                              style={this.secondaryBorder}
                            />
                          </div>
                        </div>
                        <div
                          id="top-three"
                          data-cy="top-three"
                          className="main-left-content-results"
                          draggable="false"
                        >
                          {this.renderTopThree(t)}
                          <div className="left-button-container">
                            <button
                              className={[
                                'button--primary',
                                this.props.classes.color,
                              ].join(' ')}
                              style={{ minWidth: '215px' }}
                              type="button"
                              aria-label={`${t('Explore Top 3')}. ${t(
                                'Click to start pairing degrees'
                              )}.`}
                              onClick={this.completeTopCareerSelection}
                              onKeyDown={(e) => {
                                if (['Enter'].includes(e.key))
                                  this.completeTopCareerSelection();
                              }}
                              disabled={this.topThreeIncomplete()}
                              data-cy="submit-top"
                            >
                              {t('Explore Top 3')}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="main-right-results">
                        <div className="header-container-results-right">
                          <h1 className="results-header-text">
                            {t('Explore Your Full Career List')}
                          </h1>
                          <p className="subheading not-on-mobile">
                            {t(
                              'Drag to add any of your below career matches into your top 3'
                            )}
                          </p>
                          <p className="subheading light on-mobile-only">
                            {t(
                              'Add any of your below career matches into your top 3'
                            )}
                          </p>
                          <div
                            className="yellow-border-bar"
                            style={this.secondaryBorder}
                          />
                        </div>
                        <div
                          onDrop={() => {
                            this.removeFromTopThree(this.state.draggableSource);
                          }}
                          className="main-right-content-results"
                        >
                          {this.renderOtherCareers(t)}
                        </div>
                        <div
                          className={
                            this.state.addition
                              ? 'right-button-container'
                              : 'right-button-container hidden'
                          }
                          id="right-button-container"
                        >
                          <button
                            className={[
                              'button--primary',
                              this.props.classes.color,
                            ].join(' ')}
                            style={({ minWidth: '215px' }, this.cancelStyle())}
                            type="button"
                            onClick={this.cancelAddition}
                            onKeyDown={(e) => {
                              if (['Enter'].includes(e.key))
                                this.cancelAddition();
                            }}
                          >
                            {t('Cancel')}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="top25-bar-container">
                      <ProgressBar bars={bars} tenant={this.props.tenant} />
                    </div>
                  </>
                )}
              </Fade>
            </CampusBackground>
            <ModalController />
          </>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  const tenant = state.tenant;
  const userCareerGameResults = getCareerResults(state);
  const currentLocale = state.currentLocale;
  return { auth, onboarding, tenant, userCareerGameResults, currentLocale };
};

const Top25WithRouter = withRouter(
  connect(mapStateToProps, {
    checkUser,
    getCurrentUserId,
    getData,
    getTenantCareers,
    getUserData,
    saveNewCareer,
    saveUserChoices,
    setAuthStatus,
    setUser,
    userLogout,
    setRegistrationInProgress,
  })(withStyles(continueButtonStyles)(Top25))
);

export default Top25WithRouter;
